import type {
  Participant,
  event_audio_statistic_data_change,
  event_network_quality_change,
  event_video_statistic_data_change,
} from '@zoom/videosdk';

import { logger } from '../../../../datadog/logger';
import { trySafely } from '../../../../utils';
import { LocalParticipant } from '../../../core/room/participant/LocalParticipant';
import { NetworkQualityChangeHandler } from '../../../core/room/participant/types';
import {
  ZoomNetworkQualityStatsCache,
  ZoomNetworkQualityStatsPayload,
  ZoomNetworkQualityTrigger,
  createZoomNetworkQualityStatsCache,
  updateAudioStats,
  updateVideoStats,
} from '../../stats';
import { VideoClient } from '../../types';

export class ZoomLocalParticipant implements LocalParticipant {
  #participant: Participant;
  #videoClient: VideoClient;

  constructor(participant: Participant, videoClient: VideoClient) {
    this.#participant = participant;
    this.#videoClient = videoClient;
  }

  /**
   * userIdentity is extracted from the Zoom JWT, where it is an optional property
   * We always provide user_identity in the JWT making this non-null assertion safe
   */
  get identity() {
    return this.#participant.userIdentity!;
  }

  get sid() {
    return String(this.#participant.userId);
  }

  get displayName(): string {
    return this.#participant.displayName;
  }

  on(_event: 'networkQualityLevelChanged', handler: NetworkQualityChangeHandler) {
    const stats: ZoomNetworkQualityStatsCache = createZoomNetworkQualityStatsCache();
    type Direction = Parameters<typeof event_network_quality_change>[0]['type'];

    // Zoom's `network-quality-change` fires only on overall level transitions,
    // while audio/video QoS events arrive ~1Hz per direction. We also emit from
    // those QoS handlers so the cache flushes when level stays steady, gated by
    // a throttle to keep RUM action volume reasonable.
    const EMIT_THROTTLE_MS = 5_000;
    // Stays undefined until the first `network-quality-change` arrives
    let lastKnownLevel: number | undefined;
    let lastEmitMs = 0;

    const emit = (level: number, direction: Direction, trigger: ZoomNetworkQualityTrigger) => {
      lastEmitMs = Date.now();
      const statsPayload: ZoomNetworkQualityStatsPayload = { cache: stats, level, direction, trigger };
      handler(level, statsPayload);
    };

    const emitThrottled = (direction: Direction, trigger: ZoomNetworkQualityTrigger) => {
      if (lastKnownLevel === undefined) return;
      if (Date.now() - lastEmitMs < EMIT_THROTTLE_MS) return;
      emit(lastKnownLevel, direction, trigger);
    };

    const audioStatsHandler = (payload: Parameters<typeof event_audio_statistic_data_change>[0]) => {
      updateAudioStats(stats, payload.data);
      emitThrottled(payload.data.encoding ? 'uplink' : 'downlink', 'audio-statistic-data-change');
    };

    const videoStatsHandler = (payload: Parameters<typeof event_video_statistic_data_change>[0]) => {
      updateVideoStats(stats, payload.data);
      emitThrottled(payload.data.encoding ? 'uplink' : 'downlink', 'video-statistic-data-change');
    };

    const networkQualityChangeHandler = (payload: Parameters<typeof event_network_quality_change>[0]) => {
      const { level, userId, type } = payload;
      if (userId === this.#participant.userId) {
        lastKnownLevel = level;
        emit(level, type, 'network-quality-change');
      }
    };

    this.#videoClient.on('audio-statistic-data-change', audioStatsHandler);
    this.#videoClient.on('video-statistic-data-change', videoStatsHandler);
    this.#videoClient.on('network-quality-change', networkQualityChangeHandler);

    // Zoom requires explicit subscription to begin receiving QoS data events.
    const mediaStream = this.#videoClient.getMediaStream();
    trySafely(mediaStream.subscribeAudioStatisticData(), (error) => {
      logger.error('Failed to subscribe to Zoom audio QoS data', {}, error);
    });
    trySafely(mediaStream.subscribeVideoStatisticData(), (error) => {
      logger.error('Failed to subscribe to Zoom video QoS data', {}, error);
    });

    return () => {
      this.#videoClient.off('audio-statistic-data-change', audioStatsHandler);
      this.#videoClient.off('video-statistic-data-change', videoStatsHandler);
      this.#videoClient.off('network-quality-change', networkQualityChangeHandler);

      trySafely(mediaStream.unsubscribeAudioStatisticData(), (error) => {
        logger.error('Failed to unsubscribe from Zoom audio QoS data', {}, error);
      });
      trySafely(mediaStream.unsubscribeVideoStatisticData(), (error) => {
        logger.error('Failed to unsubscribe from Zoom video QoS data', {}, error);
      });
    };
  }
}
